import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { useQueryClient } from "@tanstack/react-query";

import {
  useShippingCarriers,
  useShippingAccounts,
} from "../../hooks/shippingAccounts";
import { Loading } from "../../components";
import { UserContext } from "../../contexts";

const EMPTY_FORM = {
  carrier: "",
  label: "",
  environment: "uat",
  credentials: {},
  collection_address: {},
  tracking_url_template: "",
};

// Shipper details sent to the carrier — held per account, so multi-region
// carriers (DHL UK vs DHL France) each collect from their own address.
const COLLECTION_ADDRESS_FIELDS = [
  { key: "name", label: "Contact name" },
  { key: "address1", label: "Address line 1" },
  { key: "address2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "postcode", label: "Postcode" },
  { key: "country", label: "Country" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
];

function CollectionAddressFields({ values, onChange }) {
  return (
    <>
      <div className="ma-eb-card__span2 ma-sp-addr-head">
        <div className="ma-eb-card__label">Collection address</div>
        <div className="ma-eb-card__hint">
          Parcels for this account are collected from this address — it is sent
          to the carrier as the shipper.
        </div>
      </div>
      {COLLECTION_ADDRESS_FIELDS.map((f) => (
        <label key={f.key}>
          <div className="ma-eb-card__label">{f.label}</div>
          <input
            type="text"
            value={values?.[f.key] || ""}
            placeholder={f.label}
            autoComplete="off"
            onChange={(e) => onChange(f.key, e.target.value)}
          />
        </label>
      ))}
    </>
  );
}

function EnvBadge({ environment }) {
  if (environment === "production") {
    return (
      <span className="ma-sp-env ma-sp-env--prod">
        PRODUCTION — live, billable
      </span>
    );
  }
  return <span className="ma-sp-env ma-sp-env--uat">Sandbox (UAT)</span>;
}

function ConnectionPill({ account }) {
  const connected = account.connection_status === "connected";
  return connected ? (
    <span className="ma-sp-conn ma-sp-conn--ok">✓ Connected</span>
  ) : (
    <span className="ma-sp-conn ma-sp-conn--off">Not connected</span>
  );
}

/**
 * Dynamic credential inputs for one account, driven by the carrier registry's
 * credential_fields. Stored secrets are write-only (never echoed back) — a
 * placeholder signals a value is already saved; typing replaces it on Connect.
 */
function CredentialFields({ fields, values, shown, onChange, onToggleShow, hasStored }) {
  return (
    <>
      {fields.map((f) => (
        <label key={f.key}>
          <div className="ma-eb-card__label">{f.label}</div>
          <div className="ma-sp-cred-row">
            <input
              type={f.secret && !shown[f.key] ? "password" : "text"}
              value={values[f.key] || ""}
              autoComplete={f.secret ? "new-password" : "off"}
              data-lpignore="true"
              data-1p-ignore="true"
              placeholder={
                hasStored
                  ? "•••••••• stored — enter a new value to replace"
                  : f.label
              }
              onChange={(e) => onChange(f.key, e.target.value)}
            />
            {f.secret && (
              <button
                type="button"
                className="rc-btn rc-btn--small"
                onClick={() => onToggleShow(f.key)}
                disabled={!values[f.key]}
                title={values[f.key] ? "" : "Stored values are write-only — type a new value to reveal it"}
              >
                {shown[f.key] ? "Hide" : "Show"}
              </button>
            )}
          </div>
        </label>
      ))}
    </>
  );
}

/**
 * One shipping account card: identity + env badge + connection pill, enable
 * toggle, delete, credential editing and the Connect test.
 */
function ShippingAccountCard({ account, carrier, onChanged }) {
  const alert = useAlert();
  const [creds, setCreds] = useState({});
  const [shown, setShown] = useState({});
  const [template, setTemplate] = useState(account.tracking_url_template || "");
  const [addr, setAddr] = useState(account.collection_address || {});
  const [connecting, setConnecting] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const credentialFields = carrier?.credential_fields || [];
  const isApi = account.supports_api;

  // Re-seed local edits from the account when it changes (after Save +
  // invalidate the query returns a fresh row) so the card never shows stale
  // values. Keyed on identity + updated_at.
  useEffect(() => {
    setTemplate(account.tracking_url_template || "");
    setAddr(account.collection_address || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id, account.updated_at]);

  const onConnect = async () => {
    setInlineError("");
    setConnecting(true);
    try {
      // Push any freshly typed credentials before running the connection test.
      const typed = Object.fromEntries(
        Object.entries(creds).filter(([, v]) => v !== "" && v != null)
      );
      if (isApi && Object.keys(typed).length > 0) {
        await axios.put(`/api/shipping-accounts/${account.id}`, {
          credentials: typed,
        });
      }
      const { data } = await axios.post(
        `/api/shipping-accounts/${account.id}/connect`
      );
      if (data?.status === "ok") {
        alert.show(`${account.label} connected.`);
        setCreds({});
        setShown({});
        onChanged();
      } else {
        setInlineError(data?.message || "Connection failed.");
      }
    } catch (err) {
      setInlineError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Connection failed."
      );
    } finally {
      setConnecting(false);
    }
  };

  const onSaveAddress = async () => {
    setInlineError("");
    setSavingAddr(true);
    try {
      await axios.put(`/api/shipping-accounts/${account.id}`, {
        collection_address: addr,
      });
      alert.show("Collection address saved.");
      onChanged();
    } catch (err) {
      setInlineError(
        err?.response?.data?.message || "Failed to save the collection address."
      );
    } finally {
      setSavingAddr(false);
    }
  };

  const onSaveTemplate = async () => {
    setInlineError("");
    setSavingTemplate(true);
    try {
      await axios.put(`/api/shipping-accounts/${account.id}`, {
        tracking_url_template: template,
      });
      alert.show("Tracking URL template saved.");
      onChanged();
    } catch (err) {
      setInlineError(err?.response?.data?.message || "Failed to save template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const onToggleEnabled = async () => {
    setInlineError("");
    try {
      await axios.put(`/api/shipping-accounts/${account.id}`, {
        is_enabled: !account.is_enabled,
      });
      onChanged();
    } catch (err) {
      setInlineError(err?.response?.data?.message || "Failed to update account.");
    }
  };

  const onDelete = async () => {
    if (
      !confirm(
        `Delete ${account.label}? This removes the account and its stored credentials.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`/api/shipping-accounts/${account.id}`);
      onChanged();
    } catch (err) {
      setInlineError(err?.response?.data?.message || "Failed to delete account.");
    }
  };

  return (
    <div className="ma-mp-card ma-sp-card">
      <div className="ma-mp-card__head">
        <div className="ma-mp-card__ident">
          <div className="ma-mp-card__name">
            {account.label}
            <EnvBadge environment={account.environment} />
          </div>
          <div className="ma-mp-card__meta">
            <code>{account.carrier_name || account.carrier}</code>
            <span>· {isApi ? "API" : "Manual (tracking link only)"}</span>
            <ConnectionPill account={account} />
          </div>
        </div>
        <div className="ma-mp-card__actions">
          <button
            type="button"
            className={`rc-btn rc-btn--small${account.is_enabled ? " rc-btn--on" : ""}`}
            onClick={onToggleEnabled}
          >
            {account.is_enabled ? "On" : "Off"}
          </button>
          <button type="button" className="rc-btn rc-btn--small" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      <div className="ma-sp-card__body">
        {isApi ? (
          <div className="ma-eb-card__form-grid">
            <CredentialFields
              fields={credentialFields}
              values={creds}
              shown={shown}
              hasStored={account.has_credentials}
              onChange={(key, value) => setCreds((c) => ({ ...c, [key]: value }))}
              onToggleShow={(key) => setShown((s) => ({ ...s, [key]: !s[key] }))}
            />
            <div className="ma-eb-card__span2 ma-sp-card__connect">
              <button
                type="button"
                className="rc-btn rc-btn--small"
                onClick={onConnect}
                disabled={connecting}
              >
                {connecting
                  ? "Connecting…"
                  : account.connection_status === "connected"
                  ? "Reconnect"
                  : "Connect"}
              </button>
            </div>
            <CollectionAddressFields
              values={addr}
              onChange={(key, value) => setAddr((a) => ({ ...a, [key]: value }))}
            />
            <div className="ma-eb-card__span2 ma-sp-card__connect">
              <button
                type="button"
                className="rc-btn rc-btn--small"
                onClick={onSaveAddress}
                disabled={savingAddr}
              >
                {savingAddr ? "Saving…" : "Save address"}
              </button>
            </div>
          </div>
        ) : (
          <div className="ma-eb-card__form-grid">
            <label className="ma-eb-card__span2">
              <div className="ma-eb-card__label">Tracking URL template</div>
              <input
                type="text"
                value={template}
                placeholder="https://carrier.com/track/{tracking_number}"
                onChange={(e) => setTemplate(e.target.value)}
              />
              <div className="ma-eb-card__hint">
                {"{tracking_number}"} is replaced automatically.
              </div>
            </label>
            <div className="ma-eb-card__span2 ma-sp-card__connect">
              <button
                type="button"
                className="rc-btn rc-btn--small"
                onClick={onSaveTemplate}
                disabled={savingTemplate}
              >
                {savingTemplate ? "Saving…" : "Save template"}
              </button>
              {account.connection_status !== "connected" && (
                <button
                  type="button"
                  className="rc-btn rc-btn--small"
                  onClick={onConnect}
                  disabled={connecting}
                >
                  {connecting ? "Marking…" : "Mark as connected"}
                </button>
              )}
            </div>
          </div>
        )}
        {inlineError && <div className="ma-sp-error">{inlineError}</div>}
      </div>
    </div>
  );
}

/**
 * Master Admin → Shipping Providers.
 *
 * Per-store shipping accounts against the generalized carrier registry.
 * Multiple accounts per carrier are supported (e.g. "DHL UK" + "DHL France"),
 * each scoped to this store. Same store-resolution as MarketplaceAccountsPanel:
 * the master admin's own store (fallback: UK store for the current env).
 */
export function ShippingProvidersPanel({ user }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const queryClient = useQueryClient();
  const { activeStoreId } = useContext(UserContext);

  // The ACTIVE store (Phase 5 store-switcher), not the user's home store —
  // a multi-store owner acting in a store other than their home one must
  // manage THAT store's shipping accounts. Falls back to store_id_m for a
  // user who hasn't switched; if that's also unset, the accounts hook skips
  // the fetch (enabled: !!storeIdM) rather than defaulting to the DAO store.
  const storeIdM = activeStoreId ?? user?.store_id_m;
  const { carriers, status: carriersStatus } = useShippingCarriers();
  const { accounts, status, refetch } = useShippingAccounts(storeIdM);

  const [form, setForm] = useState(EMPTY_FORM);
  const [shownAdd, setShownAdd] = useState({});
  const [creating, setCreating] = useState(false);

  const selectedCarrier = carriers.find((c) => c.slug === form.carrier);
  const isApiCarrier = !!selectedCarrier?.supports_api;
  const environments = selectedCarrier?.environments || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["shipping-accounts", storeIdM] });
    refetch();
  };

  const onPickCarrier = (slug) => {
    const carrier = carriers.find((c) => c.slug === slug);
    setForm({
      ...EMPTY_FORM,
      carrier: slug,
      label: form.label,
      environment: carrier?.environments?.includes("uat")
        ? "uat"
        : carrier?.environments?.[0] || "uat",
      tracking_url_template: carrier?.default_tracking_url_template || "",
    });
    setShownAdd({});
  };

  const onCreate = async () => {
    if (!form.carrier || !form.label) {
      alert.show("Carrier and label are both required.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        carrier: form.carrier,
        label: form.label,
        environment: form.environment,
      };
      if (isApiCarrier) {
        const typed = Object.fromEntries(
          Object.entries(form.credentials).filter(([, v]) => v !== "" && v != null)
        );
        if (Object.keys(typed).length > 0) payload.credentials = typed;
        const addrTyped = Object.fromEntries(
          Object.entries(form.collection_address).filter(
            ([, v]) => v !== "" && v != null
          )
        );
        if (Object.keys(addrTyped).length > 0) {
          payload.collection_address = addrTyped;
        }
      } else {
        payload.tracking_url_template = form.tracking_url_template;
      }
      await axios.post(`/api/stores/${storeIdM}/shipping-accounts`, payload);
      setForm(EMPTY_FORM);
      setShownAdd({});
      invalidate();
      alert.show("Shipping account added — connect it from its card to enable it.");
    } catch (err) {
      showApiError(err, "create the shipping account");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="ma-sp">
      <p className="ma-section__hint">
        Shipping carrier accounts for this store. API carriers create real
        shipments and labels from the order flow; manual carriers only build
        tracking links. You can add several accounts per carrier (e.g. "DHL UK"
        and "DHL France") — each is scoped to this store.
      </p>

      {(status === "loading" || carriersStatus === "loading") && (
        <Loading type="small" />
      )}

      {status === "success" && accounts.length === 0 && (
        <p className="ma-section__empty">No shipping accounts yet. Add one below to start.</p>
      )}

      {status === "success" && accounts.length > 0 && (
        <div className="ma-mp-list">
          {accounts.map((a) => (
            <ShippingAccountCard
              key={a.id}
              account={a}
              carrier={carriers.find((c) => c.slug === a.carrier)}
              onChanged={invalidate}
            />
          ))}
        </div>
      )}

      <div className="ma-mp-add">
        <h4>Add shipping account</h4>
        <div className="ma-eb-card__form-grid">
          <label>
            <div className="ma-eb-card__label">Carrier</div>
            <select
              value={form.carrier}
              onChange={(e) => onPickCarrier(e.target.value)}
            >
              <option value="">Select a carrier…</option>
              {carriers.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                  {c.supports_api ? "" : " (manual tracking links)"}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="ma-eb-card__label">Label</div>
            <input
              type="text"
              value={form.label}
              placeholder="e.g. DHL UK"
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <div className="ma-eb-card__hint">
              Shown to sales reps in the order flow. Multiple accounts per
              carrier are supported — label them by region or contract.
            </div>
          </label>
          {form.carrier && environments.length > 0 && (
            <label>
              <div className="ma-eb-card__label">Environment</div>
              <select
                value={form.environment}
                onChange={(e) => setForm({ ...form, environment: e.target.value })}
              >
                {environments.map((env) => (
                  <option key={env} value={env}>
                    {env === "production"
                      ? "Production — live, billable"
                      : "Sandbox (UAT) — safe for testing"}
                  </option>
                ))}
              </select>
              {form.environment === "production" && (
                <div className="ma-eb-card__hint ma-sp-hint--prod">
                  Production creates real, billable shipments. Start on Sandbox
                  (UAT) unless you know the account is ready.
                </div>
              )}
            </label>
          )}
          {form.carrier && isApiCarrier &&
            (selectedCarrier.credential_fields || []).map((f) => (
              <label key={f.key}>
                <div className="ma-eb-card__label">{f.label}</div>
                <div className="ma-sp-cred-row">
                  <input
                    type={f.secret && !shownAdd[f.key] ? "password" : "text"}
                    value={form.credentials[f.key] || ""}
                    autoComplete={f.secret ? "new-password" : "off"}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    placeholder={f.label}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        credentials: { ...form.credentials, [f.key]: e.target.value },
                      })
                    }
                  />
                  {f.secret && (
                    <button
                      type="button"
                      className="rc-btn rc-btn--small"
                      onClick={() =>
                        setShownAdd((s) => ({ ...s, [f.key]: !s[f.key] }))
                      }
                    >
                      {shownAdd[f.key] ? "Hide" : "Show"}
                    </button>
                  )}
                </div>
              </label>
            ))}
          {form.carrier && isApiCarrier && (
            <CollectionAddressFields
              values={form.collection_address}
              onChange={(key, value) =>
                setForm((f) => ({
                  ...f,
                  collection_address: { ...f.collection_address, [key]: value },
                }))
              }
            />
          )}
          {form.carrier && !isApiCarrier && (
            <label className="ma-eb-card__span2">
              <div className="ma-eb-card__label">Tracking URL template</div>
              <input
                type="text"
                value={form.tracking_url_template}
                placeholder="https://carrier.com/track/{tracking_number}"
                onChange={(e) =>
                  setForm({ ...form, tracking_url_template: e.target.value })
                }
              />
              <div className="ma-eb-card__hint">
                {"{tracking_number}"} is replaced automatically.
              </div>
            </label>
          )}
          <div className="ma-eb-card__span2 ma-mp-add__footer">
            <span />
            <button
              type="button"
              className="rc-btn rc-btn--small"
              onClick={onCreate}
              disabled={creating}
            >
              {creating ? "Adding…" : "Add account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
